/* FH Console — Empowerment Fund
 *
 * One page, three queues:
 *   1) Pending escalations — one-tap approve / decline with optional note
 *   2) Story verification queue — verify / flag / nominate for Save of Month
 *   3) Monthly review checklist — "learn, don't police" tone, 10-min read
 *
 * Plus:
 *   - Soliciting-incident control (with rule text displayed)
 *   - Unsolicited-praise attestation flow (checkbox required)
 *
 * Backend already RBAC-scopes writes to FH's own sites.
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, Check, X, Sparkles, Trophy, MessageSquare, BookOpen, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const RULE_NO_SOLICIT = "Never ask, nudge, or survey a client for feedback or reviews. Praise only counts when it walks in by itself.";
const ATTEST_TEXT = "This praise arrived on its own — nobody asked the client.";

const REVIEW_CHECKLIST = [
    "Are the stories specific? A vague 'helped the client' rarely earns a Save.",
    "Do the flagged bill-splits have an innocent explanation? (Discuss, don't discipline.)",
    "Any wallet stuck below 40% — is the person stuck at 'is this OK?' Coach on the SPEND/DON'T/CALL framework.",
    "Anyone at 100%+ two months running? Congratulate, then check judgement is consistent.",
    "Nominate 2 candidates for Save of the Month before the 3rd.",
    "Read every story you approve. That IS the audit trail.",
    "Learn, don't police. If someone made a marginal call, coach them, don't scold.",
];

export default function FundFhConsolePage() {
    const { user } = useAuth();
    const [escalations, setEscalations] = useState([]);
    const [entries, setEntries]         = useState([]);
    const [users, setUsers]             = useState([]);
    const [loading, setLoading]         = useState(true);
    const [praiseOpen, setPraiseOpen]   = useState(false);
    const [praise, setPraise]           = useState({ user_id: "", evidence: "", attested: false });
    const [solicitOpen, setSolicitOpen] = useState(false);
    const [solicit, setSolicit]         = useState({ user_id: "", site_id: "", note: "" });

    async function load() {
        setLoading(true);
        try {
            const [eRes, entRes] = await Promise.all([
                api.get("/fund/escalations?status=pending"),
                api.get("/fund/entries"),
            ]);
            setEscalations(eRes.data || []);
            setEntries(entRes.data || []);
            try {
                const uRes = await api.get("/users");
                setUsers(uRes.data || []);
            } catch { /* non-admin FHs may not see /users */ }
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function decideEscalation(esc, action) {
        const note = action === "decline" ? (prompt("Note for the requester (optional):") ?? "") : "";
        try {
            await api.patch(`/fund/escalations/${esc.id}`, { action, note });
            toast.success(`Escalation ${action === "approve" ? "approved" : "declined"}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function reviewEntry(entry, action) {
        try {
            await api.patch(`/fund/entries/${entry.id}/review`, { action });
            toast.success(action === "verify" ? "Verified ✓" : "Flagged");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function nominate(entry, on) {
        try {
            await api.patch(`/fund/entries/${entry.id}/nominate?nominate=${on ? "true" : "false"}`);
            toast.success(on ? "Nominated for Save of the Month" : "Nomination removed");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function submitPraise() {
        if (!praise.user_id || !praise.evidence.trim() || !praise.attested) {
            toast.error("Fill subject, evidence and tick the attestation");
            return;
        }
        try {
            await api.post("/fund/stars", {
                user_id: praise.user_id, reason: "unsolicited_praise",
                evidence: praise.evidence, unsolicited_praise_attestation: true,
            });
            toast.success("+3 stars + ₹500 payroll note logged");
            setPraiseOpen(false); setPraise({ user_id: "", evidence: "", attested: false });
        } catch (e) { toast.error(apiError(e)); }
    }

    async function submitSoliciting() {
        if (!solicit.user_id || !solicit.site_id || !solicit.note.trim()) {
            toast.error("Pick a person, site, and add a note");
            return;
        }
        try {
            await api.post("/fund/soliciting-incident", solicit);
            toast.success("Recorded. Owner notified. Month stars zeroed for this user.");
            setSolicitOpen(false); setSolicit({ user_id: "", site_id: "", note: "" });
        } catch (e) { toast.error(apiError(e)); }
    }

    const toReview = useMemo(() => entries.filter((e) => e.status === "logged" || e.status === "flagged"), [entries]);
    const verified = useMemo(() => entries.filter((e) => e.status === "verified"), [entries]);

    if (loading) return <div className="mx-auto max-w-5xl p-6"><div className="h-64 rounded-2xl bg-white animate-pulse" /></div>;

    const myUsers = users.filter((u) => (u.role === "SITE_MANAGER" || u.role === "PROJECT_MANAGER"));

    return (
        <div className="mx-auto max-w-5xl px-4 py-6" data-testid="fund-fh-console">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-3xl font-bold text-[#1E3355] inline-flex items-center gap-2"><ShieldAlert className="w-7 h-7 text-amber-500" /> FH · Empowerment Fund</h1>
                    <div className="text-sm text-gray-500">Learn, don't police — a 10-minute monthly read.</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setPraiseOpen(true)}
                        data-testid="fund-fh-log-praise-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2"
                    >
                        <ThumbsUp className="w-4 h-4" /> Log unsolicited praise
                    </button>
                    <button
                        onClick={() => setSolicitOpen(true)}
                        data-testid="fund-fh-solicit-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-300 text-red-800 hover:bg-red-50 text-sm font-semibold px-4 py-2"
                    >
                        <ShieldAlert className="w-4 h-4" /> Soliciting incident
                    </button>
                </div>
            </div>

            {/* Escalations */}
            <section className="mt-6" data-testid="fund-fh-escalations">
                <h2 className="text-sm uppercase tracking-widest text-gray-500 font-bold mb-2">Pending escalations ({escalations.length})</h2>
                {escalations.length === 0 && <div className="rounded-xl bg-white border border-gray-100 p-4 text-gray-400 italic">Nothing pending. 🎉</div>}
                <div className="space-y-2">
                    {escalations.map((e) => (
                        <div key={e.id} className="rounded-2xl bg-white border border-gray-100 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`fund-fh-esc-${e.id}`}>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-lg font-bold text-[#1E3355]">₹{Number(e.proposed_amount).toLocaleString("en-IN")}</span>
                                    <span className="text-xs rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">{e.requester_name}</span>
                                    <span className="text-[10px] text-gray-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                </div>
                                <div className="italic text-gray-700 mt-1">"{e.reason}"</div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button
                                    onClick={() => decideEscalation(e, "approve")}
                                    data-testid={`fund-fh-esc-approve-${e.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2"
                                >
                                    <Check className="w-4 h-4" /> Approve
                                </button>
                                <button
                                    onClick={() => decideEscalation(e, "decline")}
                                    data-testid={`fund-fh-esc-decline-${e.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-300 text-red-800 hover:bg-red-50 text-sm font-semibold px-4 py-2"
                                >
                                    <X className="w-4 h-4" /> Decline
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Verification queue */}
            <section className="mt-8" data-testid="fund-fh-review-queue">
                <h2 className="text-sm uppercase tracking-widest text-gray-500 font-bold mb-2 inline-flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Story verification queue ({toReview.length})</h2>
                {toReview.length === 0 && <div className="rounded-xl bg-white border border-gray-100 p-4 text-gray-400 italic">Nothing to review right now.</div>}
                <div className="space-y-2">
                    {toReview.map((e) => (
                        <div key={e.id} className="rounded-2xl bg-white border border-gray-100 p-4" data-testid={`fund-fh-entry-${e.id}`}>
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="font-medium text-[#1E3355]">{e.client_or_tenant} <span className="text-gray-400 font-normal">· {e.logged_by_name}</span></div>
                                <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        e.status === "flagged" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"
                                    }`}>{e.status.toUpperCase()}</span>
                                    <span className="text-sm font-bold text-emerald-700">₹{Number(e.amount).toLocaleString("en-IN")}</span>
                                </div>
                            </div>
                            <div className="italic text-gray-700 mt-1">"{e.one_line_story}"</div>
                            {e.flag_reason && <div className="text-[11px] text-red-700 mt-1 italic">Flag: {e.flag_reason}</div>}
                            <div className="mt-2 flex gap-2 flex-wrap">
                                <button onClick={() => reviewEntry(e, "verify")} data-testid={`fund-fh-verify-${e.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5"><Check className="w-3.5 h-3.5" /> Verify</button>
                                <button onClick={() => reviewEntry(e, "flag")}   data-testid={`fund-fh-flag-${e.id}`} className="inline-flex items-center gap-1 rounded-full bg-white border border-red-300 text-red-800 text-xs font-semibold px-3 py-1.5"><X className="w-3.5 h-3.5" /> Flag</button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Verified — nominate for Save of Month */}
            {verified.length > 0 && (
                <section className="mt-8" data-testid="fund-fh-nominate">
                    <h2 className="text-sm uppercase tracking-widest text-gray-500 font-bold mb-2 inline-flex items-center gap-1.5"><Trophy className="w-4 h-4 text-amber-500" /> Nominate — Save of the Month</h2>
                    <div className="space-y-2">
                        {verified.map((e) => (
                            <div key={e.id} className="rounded-2xl bg-white border border-gray-100 p-3 flex items-center gap-3" data-testid={`fund-fh-nominee-${e.id}`}>
                                <div className="flex-1">
                                    <div className="font-medium text-[#1E3355]">{e.client_or_tenant} <span className="text-gray-400 font-normal">· {e.logged_by_name}</span></div>
                                    <div className="italic text-gray-700 text-xs mt-0.5">"{e.one_line_story}"</div>
                                </div>
                                <button
                                    onClick={() => nominate(e, !e.nominated)}
                                    data-testid={`fund-fh-nominate-toggle-${e.id}`}
                                    className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold px-3 py-1.5 ${
                                        e.nominated ? "bg-amber-500 text-white" : "bg-white border border-amber-300 text-amber-800 hover:bg-amber-50"
                                    }`}
                                >
                                    <Sparkles className="w-3.5 h-3.5" /> {e.nominated ? "Nominated" : "Nominate"}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Monthly review checklist — 'learn, don't police' */}
            <section className="mt-8 rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-200 p-5" data-testid="fund-fh-checklist">
                <h2 className="text-sm uppercase tracking-widest text-violet-800 font-bold mb-2 inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> Monthly review — a 10-minute read (learn, don't police)</h2>
                <ol className="space-y-1.5 text-sm text-gray-800 list-decimal ml-5">
                    {REVIEW_CHECKLIST.map((x, i) => <li key={i}>{x}</li>)}
                </ol>
            </section>

            {/* Unsolicited praise modal */}
            {praiseOpen && (
                <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setPraiseOpen(false)} data-testid="fund-fh-praise-modal">
                    <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-3xl sm:rounded-2xl bg-white p-5 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-lg font-bold text-[#1E3355] inline-flex items-center gap-2"><ThumbsUp className="w-5 h-5 text-emerald-600" /> Unsolicited praise</div>
                            <button onClick={() => setPraiseOpen(false)}><X className="w-6 h-6 text-gray-400" /></button>
                        </div>
                        <div className="text-xs italic text-red-800 bg-red-50 border-l-4 border-red-500 rounded-r p-2 mb-3">{RULE_NO_SOLICIT}</div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Praised staff member</label>
                        <select value={praise.user_id} onChange={(e) => setPraise((p) => ({ ...p, user_id: e.target.value }))} className="w-full rounded-xl border border-gray-200 px-3 py-2 mb-3" data-testid="fund-fh-praise-user">
                            <option value="">— pick —</option>
                            {myUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                        </select>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">What did the client say / write?</label>
                        <textarea value={praise.evidence} onChange={(e) => setPraise((p) => ({ ...p, evidence: e.target.value }))} rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2 mb-3" data-testid="fund-fh-praise-evidence" />
                        <label className="flex items-start gap-2 text-sm mb-4">
                            <input type="checkbox" checked={praise.attested} onChange={(e) => setPraise((p) => ({ ...p, attested: e.target.checked }))} data-testid="fund-fh-praise-attest" className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600" />
                            <span className="italic text-gray-700">{ATTEST_TEXT}</span>
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setPraiseOpen(false)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitPraise} disabled={!praise.attested} data-testid="fund-fh-praise-submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-5 py-2">Log +3 stars</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Soliciting incident modal */}
            {solicitOpen && (
                <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setSolicitOpen(false)} data-testid="fund-fh-solicit-modal">
                    <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-3xl sm:rounded-2xl bg-white p-5 shadow-2xl">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-lg font-bold text-[#1E3355] inline-flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-600" /> Log soliciting incident</div>
                            <button onClick={() => setSolicitOpen(false)}><X className="w-6 h-6 text-gray-400" /></button>
                        </div>
                        <div className="text-xs italic text-red-800 bg-red-50 border-l-4 border-red-500 rounded-r p-2 mb-3">{RULE_NO_SOLICIT}</div>
                        <p className="text-xs text-gray-600 mb-3">Logging this will <b>zero all of this user's stars for the month</b> and notify the Owner.</p>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Person</label>
                        <select value={solicit.user_id} onChange={(e) => {
                            const u = users.find((x) => x.id === e.target.value);
                            setSolicit((s) => ({ ...s, user_id: e.target.value, site_id: (u?.site_ids || [])[0] || "" }));
                        }} className="w-full rounded-xl border border-gray-200 px-3 py-2 mb-3" data-testid="fund-fh-solicit-user">
                            <option value="">— pick —</option>
                            {myUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Note (what happened?)</label>
                        <textarea value={solicit.note} onChange={(e) => setSolicit((s) => ({ ...s, note: e.target.value }))} rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2 mb-3" data-testid="fund-fh-solicit-note" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setSolicitOpen(false)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitSoliciting} data-testid="fund-fh-solicit-submit" className="rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2">Confirm — zero stars</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
